import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface LogItem {
  ts: string
  category?: string
  question?: string
  contexts?: number
  provider_requested?: string | null
  provider_used?: string | null
  duration_ms?: number
  error?: string | null
}

export default function LogsPage() {
  const [items, setItems] = useState<LogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/logs')
      setItems(data as LogItem[])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clear = async () => {
    try {
      await api.delete('/logs')
      setItems([])
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur purge logs')
    }
  }

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 px-1 py-3 sm:px-6 sm:py-6">
        <div className="mb-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Retour
          </button>
        </div>
        <section className="space-y-6 rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <div className="flex items-center justify-end gap-2">
            <button onClick={load} className="rounded-full border border-emerald-200/70 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-100 dark:border-emerald-900/60 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50" disabled={loading}>
              {loading ? 'Chargement…' : 'Actualiser'}
            </button>
            <button onClick={clear} className="rounded-full border border-red-200/70 bg-red-50/80 px-4 py-2 text-sm font-semibold text-red-700 shadow-sm transition hover:bg-red-100 dark:border-red-900/60 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/50">
              Purger
            </button>
          </div>

          {error && <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">{error}</div>}

          <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left border-b border-zinc-200 dark:border-zinc-800">
            <tr>
              <th className="py-2 pr-4">Horodatage</th>
              <th className="py-2 pr-4">Req</th>
              <th className="py-2 pr-4">Used</th>
              <th className="py-2 pr-4">Durée</th>
              <th className="py-2 pr-4">Ctx</th>
              <th className="py-2 pr-4">Catégorie/Cours</th>
              <th className="py-2 pr-4">Question</th>
              <th className="py-2 pr-4">Erreur</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={idx} className="border-b border-zinc-100 dark:border-zinc-800">
                <td className="py-2 pr-4 whitespace-nowrap text-zinc-500">{it.ts}</td>
                <td className="py-2 pr-4">{it.provider_requested || ''}</td>
                <td className="py-2 pr-4">{it.provider_used || ''}</td>
                <td className="py-2 pr-4 whitespace-nowrap">{typeof it.duration_ms === 'number' ? `${it.duration_ms} ms` : ''}</td>
                <td className="py-2 pr-4">{typeof it.contexts === 'number' ? it.contexts : ''}</td>
                <td className="py-2 pr-4">{it.category || ''}</td>
                <td className="py-2 pr-4 max-w-[480px] truncate" title={it.question || ''}>{it.question || ''}</td>
                <td className="py-2 pr-4 text-red-600">{it.error || ''}</td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr>
                <td className="py-6 text-zinc-500" colSpan={8}>Aucun log pour le moment.</td>
              </tr>
            )}
          </tbody>
        </table>
          </div>
        </section>
      </div>
    </div>
  )
}
